import React from 'react';
import { formatAmount } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { GstBreakupDisplay } from '../../SharedDomainComponents/GstBreakupDisplay/GstBreakupDisplay';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import type { BillSummaryProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Currency formatting (symbol, separators, decimals, subunit factor) is owned
// by @bbpos/utils. Only the sign is placed before the symbol here so a negative
// round-off renders "-₹0.50" (not "₹-0.50"), preserving the prior INR output.
function formatMoney(amount: number, currency: CurrencyCode): string {
  const sign = amount < 0 ? '-' : '';
  return `${sign}${formatAmount(Math.abs(amount), currency)}`;
}

function Row({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={`flex justify-between text-sm ${className ?? ''}`}>
      <dt className="text-neutral-600 dark:text-neutral-400">{label}</dt>
      <dd className="font-medium text-neutral-800 dark:text-neutral-100">{value}</dd>
    </div>
  );
}

// ─── BillSummary ──────────────────────────────────────────────────────────────

export function BillSummary({
  subtotal,
  discountAmount = 0,
  billDiscountAmount = 0,
  cgst,
  sgst,
  igst,
  taxSlabs,
  roundOff = 0,
  total,
  pendingDiscountApproval,
  currency = 'INR',
  taxInclusive = false,
  taxNote,
  className,
}: BillSummaryProps) {
  const totalTax = cgst + sgst + igst;

  // Resolve the tenant's tax regime so the breakup renders the correct
  // vocabulary (GST/CGST/SGST/IGST for India, single VAT line for GCC).
  const taxRegime = resolveTaxRegime(currency);

  // Build GST slabs for GstBreakupDisplay. The taxable base is the gross
  // subtotal minus BOTH per-line discounts and the bill-level discount, so
  // the displayed taxable amount matches what tax was actually computed on.
  const taxableAmount = subtotal - discountAmount - billDiscountAmount;
  const isInterState = igst > 0;
  // Derive the slab rate from actual tax / taxable so the label reflects the
  // tenant's configured rate (e.g. 5% VAT for Oman) instead of a hardcoded
  // 18% GST literal. Falls back to 0 when there's nothing to divide by.
  const derivedRate =
    taxableAmount > 0 ? Math.round((totalTax / taxableAmount) * 100) : 0;
  // A mixed-rate cart passes its per-rate slabs, so each rate gets its own
  // line rather than a blended rate that is no real slab (QA T1-003).
  // Zero-tax slabs are dropped: the breakup only lists tax actually charged.
  const gstSlabs = taxSlabs
    ? taxSlabs
        .filter((s) => s.cgst + s.sgst + s.igst > 0)
        .map((s) => ({ ...s, items: [] }))
    : isInterState
      ? [{ rate: derivedRate, taxableAmount, igst, cgst: 0, sgst: 0, items: [] }]
      : [{ rate: derivedRate, taxableAmount, igst: 0, cgst, sgst, items: [] }];

  return (
    <dl className={`space-y-2 ${className ?? ''}`}>
      <Row label="Subtotal" value={formatMoney(subtotal, currency)} />

      {discountAmount > 0 && (
        <Row
          label="Discount"
          value={`-${formatMoney(discountAmount, currency)}`}
          className="text-success-700 dark:text-success-400"
        />
      )}

      {billDiscountAmount > 0 && (
        <Row
          label="Bill Discount"
          value={`-${formatMoney(billDiscountAmount, currency)}`}
          className="text-success-700 dark:text-success-400"
        />
      )}

      {pendingDiscountApproval && (
        <div className="flex justify-between text-sm">
          <dt className="text-warning-600 dark:text-warning-400 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-warning-500 animate-pulse" />
            Discount (pending)
          </dt>
          <dd className="font-medium text-warning-600 dark:text-warning-400 tabular-nums animate-pulse">
            {pendingDiscountApproval.type === 'PERCENTAGE'
              ? `-${pendingDiscountApproval.value}%`
              : `-${formatMoney(pendingDiscountApproval.value, currency)}`}
          </dd>
        </div>
      )}

      {taxNote && (
        <p role="note" className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
          {taxNote}
        </p>
      )}

      {totalTax > 0 && (
        <div>
          <GstBreakupDisplay
            slabs={gstSlabs}
            isInterState={isInterState}
            totalTax={totalTax}
            loading={false}
            variant="compact"
            taxRegime={taxRegime}
            currency={currency}
          />
          {taxInclusive && (
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
              Catalogue prices are shown inclusive of tax; the total is the same either way.
            </p>
          )}
        </div>
      )}

      {roundOff !== 0 && (
        <Row label="Round Off" value={formatMoney(roundOff, currency)} />
      )}

      <div className="border-t border-neutral-200 dark:border-neutral-600 pt-2">
        <div className="flex justify-between text-base font-bold text-neutral-900 dark:text-white">
          <dt>Total</dt>
          <dd aria-live="polite">{formatMoney(total, currency)}</dd>
        </div>
      </div>
    </dl>
  );
}
